// Per-channel composer drafts: whatever was typed into a channel's message box and not sent yet,
// kept so that it's still there after switching to another channel and back, and after an app
// restart. One draft per channel id, never shared between channels; the mode it was written in
// (plain textarea or the Markdown editor) is remembered with it, so it reopens in the same editor.
//
// Stored in localStorage like the rest of the client-local state (see settings/appSettings.ts),
// under its own key - drafts change on every keystroke and have nothing to do with settings.
// Reads come from an in-memory copy; writes are debounced and flushed when it matters (channel
// switch, composer unmount, the page going away).
//
// Plain module, no Vue/Tauri imports: the composer (`useChatComposer.ts`) is the only consumer.

const DRAFTS_STORAGE_KEY = "iopta:composer-drafts"
const WRITE_DEBOUNCE_MS = 300
/** Oldest drafts are dropped past this many channels, so the store can't grow without bound. */
const MAX_DRAFTS = 200
/** Well above the message length limit - the Markdown editor can hold more than can be sent. */
const MAX_DRAFT_LENGTH = 20_000

export interface ComposerDraft {
  text: string
  /** Written in the Markdown editor rather than the plain textarea. */
  markdown: boolean
}

interface StoredDraft extends ComposerDraft {
  updatedAt: number
}

let drafts: Record<string, StoredDraft> | null = null
let writeTimer: ReturnType<typeof setTimeout> | null = null

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null
  } catch {
    return null
  }
}

function load(): Record<string, StoredDraft> {
  if (drafts) return drafts
  drafts = {}
  try {
    const raw = storage()?.getItem(DRAFTS_STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      for (const [channelId, value] of Object.entries(parsed as Record<string, unknown>)) {
        const draft = value as Partial<StoredDraft> | null
        if (!draft || typeof draft.text !== "string" || draft.text.length === 0) continue
        drafts[channelId] = {
          text: draft.text,
          markdown: draft.markdown === true,
          updatedAt: typeof draft.updatedAt === "number" ? draft.updatedAt : 0,
        }
      }
    }
  } catch (error) {
    console.warn("[composerDrafts] Failed to read drafts:", error)
  }
  return drafts
}

function write() {
  writeTimer = null
  if (!drafts) return
  try {
    const store = storage()
    if (!store) return
    if (Object.keys(drafts).length === 0) store.removeItem(DRAFTS_STORAGE_KEY)
    else store.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts))
  } catch (error) {
    console.warn("[composerDrafts] Failed to save drafts:", error)
  }
}

function scheduleWrite() {
  if (writeTimer) return
  writeTimer = setTimeout(write, WRITE_DEBOUNCE_MS)
}

/** Writes any pending change to storage right now. */
export function flushComposerDrafts() {
  if (!writeTimer) return
  clearTimeout(writeTimer)
  write()
}

export function getComposerDraft(channelId: string): ComposerDraft | null {
  const draft = load()[channelId]
  return draft ? { text: draft.text, markdown: draft.markdown } : null
}

/** Remembers the channel's unsent text; an empty (whitespace-only) text forgets the draft. */
export function setComposerDraft(channelId: string, draft: ComposerDraft) {
  const all = load()
  const existing = all[channelId]
  if (draft.text.trim().length === 0) {
    if (!existing) return
    delete all[channelId]
    scheduleWrite()
    return
  }
  const text = draft.text.slice(0, MAX_DRAFT_LENGTH)
  if (existing && existing.text === text && existing.markdown === draft.markdown) return
  all[channelId] = { text, markdown: draft.markdown, updatedAt: Date.now() }

  const ids = Object.keys(all)
  if (ids.length > MAX_DRAFTS) {
    ids.sort((a, b) => all[a]!.updatedAt - all[b]!.updatedAt)
    for (const id of ids.slice(0, ids.length - MAX_DRAFTS)) delete all[id]
  }
  scheduleWrite()
}

/** Logout: unsent text doesn't carry over to whoever signs in next on this device. */
export function clearComposerDrafts() {
  drafts = {}
  if (writeTimer) clearTimeout(writeTimer)
  write()
}

// The debounce must not cost the last few keystrokes when the app is closed or backgrounded
// (mobile webviews are suspended without any unload event - `pagehide`/`visibilitychange` are the
// ones that do fire).
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushComposerDrafts)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushComposerDrafts()
  })
}
